import { Redirect, Route, Switch, } from "react-router-dom";

import {
    PageForbidden,
    PageNotFound
} from "pages/errors";

import {
    DirectionPage,
    Home,
    ListContents,
    Login,
    PlayerPage,
    Registration,
    Subscribe,
    UserProfile,
} from "pages";

import {
    ChooseAction,
    CreateContent,
    CreateContentList,
    CreateSubscription,
    CreateUser,
    EditContent,
    EditContentList,
    EditSubscription,
    EditUser,
    ManageActions,
    ManageAward,
    ManageContent,
    ManageContentList,
    ManageCountry,
    ManageCoupon,
    ManageFestival,
    ManageGenre,
    ManageLanguage,
    ManageSubscription,
    ManageTag,
    ManageUser,
    SubscriptionActions
} from "pages/backoffice";

import {
    PaymentError,
    PaymentSuccess
} from "pages/payment";

import createSeries from "pages/backoffice/manage-series/create-series/create-series";
import editSeries from "pages/backoffice/manage-series/edit-series/edit-series";
import manageSeries from "pages/backoffice/manage-series/manage-series";
import AuthenticationService from "services/authentication-service";


interface ProtectedRouteProps {
    isOnlyAdmin?: boolean;
    isAtLeastProducer?: boolean;
}

export class ProtectedRoute extends Route<ProtectedRouteProps> {

    static defaultProps = {
        isOnlyAdmin: false,
        isAtLeastProducer: false
    };

    async componentDidUpdate() {

        await AuthenticationService.updateCurrentUserWithPromise();
    }

    render() {

        if (AuthenticationService.isNotAtLeastProducer() && this.props.isAtLeastProducer) {
            return (<Redirect to={"/login?redirectReason=no_privileges"} />);
        }

        if (AuthenticationService.isNotAdmin() && this.props.isOnlyAdmin) {
            return (<Redirect to={"/login?redirectReason=no_privileges"} />);
        }

        if (AuthenticationService.isExpired()) {
            return (<Redirect to={"/login?redirectReason=expired"} />);
        }

        if (!AuthenticationService.isLoggedIn()) {
            return (<Redirect to={"/login?redirectReason=auth_required"} />);
        }

        return super.render();
    }
}

let allowPublicContent = process.env.REACT_APP_ALLOW_PUBLIC_CONTENT === "true"

export class MandatorySubscriptionRoute extends ProtectedRoute {

    render() {
        if (allowPublicContent)
            return super.render();

        let currentUser = AuthenticationService.getLocalCurrentUser();

        if (currentUser === null || !currentUser.subscriptionId)
            return (<Redirect to={"/subscribe"} />);

        return super.render();
    }
}

let backofficeMode = process.env.REACT_APP_BACKOFFICE_MODE === "true"

const routes = () => (
    <Switch>
        {allowPublicContent && <Route exact path="/" component={Home} />}
        {!allowPublicContent && <ProtectedRoute exact path="/" component={Home} />}
        {!backofficeMode && <>
            <ProtectedRoute exact path="/subscribe" component={Subscribe} />
            {!allowPublicContent && <MandatorySubscriptionRoute exact path="/" component={Home} />}
            {allowPublicContent && <Route exact path="/player/:contentId" component={PlayerPage} />}
            {!allowPublicContent && <ProtectedRoute exact path="/player/:contentId" component={PlayerPage} />}
            {allowPublicContent && <Route exact path="/contents/list" component={ListContents} />}
            {!allowPublicContent && <ProtectedRoute exact path="/contents/list" component={ListContents} />}
            <ProtectedRoute isOnlyAdmin={true} exact path="/direction/:contentId" component={DirectionPage} />
            <ProtectedRoute path="/user-profile" component={UserProfile} />
            <Route exact path="/checkout/success" component={PaymentSuccess} />
            <Route exact path="/checkout/error" component={PaymentError} />
        </>}
        <Route path="/forbidden" component={PageForbidden} />
        <Route exact path="/login" component={Login} />
        <Route exact path="/registration" component={Registration} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/choose-action" component={ChooseAction} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/manage-actions" component={ManageActions} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/subscription-actions" component={SubscriptionActions} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/content-upload" component={CreateContent} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/manage-list" component={ManageContentList} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/manage-content" component={ManageContent} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/edit-content/:id" component={EditContent} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/edit-series/:id" component={editSeries} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/series" component={manageSeries} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/create-series" component={createSeries} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/manage-tag" component={ManageTag} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/manage-festival" component={ManageFestival} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/manage-award" component={ManageAward} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/manage-genre" component={ManageGenre} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/manage-country" component={ManageCountry} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/manage-language" component={ManageLanguage} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/manage-coupon" component={ManageCoupon} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/manage-subscription" component={ManageSubscription} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/manage-user" component={ManageUser} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/create-content-list" component={CreateContentList} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/edit-content-list/:id" component={EditContentList} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/create-subscription" component={CreateSubscription} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/edit-subscription/:id" component={EditSubscription} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/create-user" component={CreateUser} />
        <ProtectedRoute isAtLeastProducer={true} path="/backoffice/edit-user/:id" component={EditUser} />
        <Route path="*" component={PageNotFound} />
    </Switch>);

export default routes;
